/**
 * Documentos del SAT (Constancia de Situación Fiscal y Opinión del cumplimiento): el seguimiento de una descarga en
 * la pantalla (window.FDSatdocSeguimiento). Funciones puras (Vitest en entorno node, sin DOM ni jQuery); el montaje
 * vive en application/views/noble/satdocumentos/index.php.
 *
 * Por qué existe: la pantalla perdía el progreso al cambiar de pestaña (las suyas o las del navegador) o al salir y
 * volver, y el usuario, creyendo que no lo había pedido, volvía a dar clic. Cada clic de más es otra solicitud al SAT
 * con su contraseña (CIEC). Ahora la pantalla le pregunta al servidor (v2/satdocumentos/seguimiento_ajax) qué hay en
 * curso al cargar, al volver a la pestaña y al cambiar entre Constancia y Opinión, y con eso decide qué pinta.
 *
 * Lo que entra es el `seguimiento` del servidor, por tipo (Satdocumentos::_seguimiento): { id, tipo, estatus,
 * en_curso, origen, mensaje, desde_hhmm, seg_en_curso, fin_hace_seg, en_pausa, pausa, documento_id, error_msg,
 * url_credencial }, o null si nunca se pidió. El servidor es quien dice si sigue en curso (`en_curso`): esta capa no
 * vuelve a deducirlo del estatus, y el servidor tampoco confía en ella (un clic de más ya no encola otra solicitud).
 */

/** Cada cuánto se pregunta mientras hay una descarga en curso y la pestaña está a la vista. */
export const SONDEO_MS = 5000;
/** Cuando ya tardó más de lo normal: se sigue preguntando, más despacio. */
export const SONDEO_LENTO_MS = 10000;
/** Una automática que espera la pausa de la CIEC: una vez por minuto basta (la pausa dura horas). */
export const SONDEO_PAUSA_MS = 60000;
/** Desde aquí se avisa con calma que está tardando (el mismo tope de 5 min que tenía el aviso anterior). */
export const TARDANDO_SEG = 300;
/** El resultado de una descarga que la pantalla seguía se anuncia si terminó hace menos de esto. */
export const RESULTADO_VIGENTE_SEG = 3600;
/** Al volver a la pestaña (focus y visibilitychange llegan juntos) no se pregunta dos veces seguidas. */
export const MIN_ENTRE_CONSULTAS_MS = 1500;

const DOC = { csf: 'tu constancia', opinion: 'tu opinión del cumplimiento' };
const DOC_LISTO = { csf: 'tu Constancia de Situación Fiscal', opinion: 'tu Opinión del cumplimiento' };

export const TEXTO_TARDANDO = 'Está tardando más de lo normal y sigue en camino; aquí te avisamos en cuanto termine.';
export const TEXTO_FALLO = 'Esta vez no logramos descargar tu documento del Portal SAT. Inténtalo de nuevo en un rato o sube el PDF que ya tengas.';

/** Un valor de un catálogo por su clave PROPIA (`DOC['constructor']` no es del catálogo). */
function de(catalogo, clave, otro) {
  return Object.prototype.hasOwnProperty.call(catalogo, clave) ? catalogo[clave] : otro;
}

/** Un número, o null (también para null, undefined y '': `Number(null)` sería 0, y 0 sí es un dato). */
function num(v) {
  if (v === null || v === undefined || v === '') { return null; }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** El texto principal mientras se obtiene el documento. */
export function textoProgreso(tipo) {
  return 'Estamos obteniendo ' + de(DOC, tipo, 'tu documento') + ' del SAT; puede tardar un par de minutos. '
    + 'Puedes seguir trabajando y regresar aquí.';
}

/** La línea chica de debajo: el paso en que va (lo escribe el worker) y desde qué hora. */
export function detalle(s) {
  if (!s) { return ''; }
  const partes = [];
  if (s.mensaje) { partes.push(String(s.mensaje)); }
  if (s.desde_hhmm) { partes.push('en curso desde las ' + String(s.desde_hhmm)); }
  return partes.join(' · ');
}

/**
 * Una AUTOMÁTICA (o el reintento de una que pidió el usuario) que espera la pausa de logins con CIEC: hasta qué hora,
 * que sigue sola y que, si la necesita ya, la puede pedir él (el servidor toma esa misma solicitud: no encola otra).
 */
export function textoPausa(s) {
  const reintento = !!(s && s.origen === 'manual');
  const hasta = s && s.pausa && s.pausa.hasta_hhmm ? ' hasta las ' + String(s.pausa.hasta_hhmm) : '';
  return (reintento ? 'El reintento de tu descarga' : 'Tu descarga automática') + ' está en pausa' + hasta
    + ' y sigue ' + (reintento ? 'solo' : 'sola') + ' en cuanto termine. Si la necesitas ya, descárgala tú.';
}

export function esTerminal(s) {
  return !!s && (s.estatus === 'completado' || s.estatus === 'error');
}

/**
 * Qué pinta la pantalla para la solicitud de UNA pestaña.
 *
 * @param {object|null} s       el seguimiento del servidor para ese tipo
 * @param {object} [o]          { seguida: id de la solicitud que esta pantalla sigue (la que pidió, o la que vio en curso) }
 * @returns {{modo: string, bloquea: boolean, texto: string, detalle: string, tardando: boolean, cta: (string|null)}}
 *   modo: 'libre' (nada en curso), 'progreso' (en curso: el botón espera), 'pausa' (una automática en pausa: el botón
 *   sigue libre), 'listo' / 'fallo' (terminó la que se seguía: se anuncia una vez).
 */
export function vista(s, o) {
  const libre = { modo: 'libre', bloquea: false, texto: '', detalle: '', tardando: false, cta: null };
  if (!s || typeof s !== 'object') { return libre; }
  if (s.en_curso && s.en_pausa) {
    return { modo: 'pausa', bloquea: false, texto: textoPausa(s), detalle: '', tardando: false, cta: null };
  }
  if (s.en_curso) {
    const tardando = (num(s.seg_en_curso) || 0) >= TARDANDO_SEG;
    return { modo: 'progreso', bloquea: true, texto: textoProgreso(s.tipo), detalle: detalle(s), tardando: tardando, cta: null };
  }
  const seguida = o && o.seguida !== undefined && o.seguida !== null ? num(o.seguida) : null;
  const fin = num(s.fin_hace_seg);
  if (seguida !== null && num(s.id) === seguida && esTerminal(s) && fin !== null && fin <= RESULTADO_VIGENTE_SEG) {
    if (s.estatus === 'completado') {
      return { modo: 'listo', bloquea: false, texto: 'Listo: ' + de(DOC_LISTO, s.tipo, 'tu documento') + ' ya está aquí.',
        detalle: '', tardando: false, cta: null };
    }
    return { modo: 'fallo', bloquea: false, texto: (s.error_msg ? String(s.error_msg) : TEXTO_FALLO), detalle: '',
      tardando: false, cta: s.url_credencial ? String(s.url_credencial) : null };
  }
  return libre;
}

/**
 * La solicitud que la pantalla debe seguir para un tipo, a partir de lo que ya seguía y de lo que dice el servidor:
 * la que está en curso pasa a ser la seguida (aunque la haya pedido otra persona de la cuenta o la automatización:
 * su final también se anuncia); si no hay ninguna en curso, se conserva la que se seguía hasta anunciarla.
 */
export function seguidaTras(seguida, s) {
  if (s && s.en_curso && num(s.id) !== null) { return num(s.id); }
  return (seguida === undefined || seguida === null) ? null : num(seguida);
}

/**
 * ¿Hay que soltar la seguida sin anunciarla? Sí cuando el servidor ya no la reporta (otra más nueva terminó después)
 * o cuando terminó hace demasiado para anunciarla: así una seguida vieja de localStorage no se queda para siempre.
 */
export function soltarSeguida(seguida, s) {
  if (seguida === undefined || seguida === null) { return false; }
  if (!s) { return true; }
  if (s.en_curso) { return false; }
  if (num(s.id) !== num(seguida)) { return true; }
  const fin = num(s.fin_hace_seg);
  return !esTerminal(s) || fin === null || fin > RESULTADO_VIGENTE_SEG;
}

/**
 * Cada cuánto volver a preguntar, o null para no preguntar: nada en curso, o la pestaña del navegador oculta (al
 * volver se pregunta en el acto). Mira LOS DOS tipos: una descarga sigue aunque la persona esté en la otra pestaña.
 */
export function proximoSondeo(seguimiento, visible) {
  if (!visible || !seguimiento || typeof seguimiento !== 'object') { return null; }
  let ms = null;
  Object.keys(seguimiento).forEach(function (tipo) {
    const s = seguimiento[tipo];
    if (!s || !s.en_curso) { return; }
    const m = s.en_pausa ? SONDEO_PAUSA_MS : ((num(s.seg_en_curso) || 0) >= TARDANDO_SEG ? SONDEO_LENTO_MS : SONDEO_MS);
    ms = (ms === null) ? m : Math.min(ms, m);
  });
  return ms;
}

/** ¿Se puede preguntar ya? (focus y visibilitychange llegan casi juntos; una consulta en vuelo no se duplica). */
export function puedeConsultar(ultimaMs, ahoraMs, enVuelo) {
  if (enVuelo) { return false; }
  const u = num(ultimaMs);
  return u === null || (num(ahoraMs) || 0) - u >= MIN_ENTRE_CONSULTAS_MS;
}

/**
 * ¿«Descargar del SAT» se puede oprimir? No con un aviso que lo apague (RFC genérico, conexión con la CIEC sin
 * activar), mientras el clic anterior sigue en vuelo, ni con una descarga en curso de ese tipo (sí con una en pausa).
 */
export function botonActivo(o) {
  return !(o && (o.aviso || o.enviando || (o.vista && o.vista.bloquea)));
}

/** Llave de localStorage de la solicitud seguida, por cuenta y tipo: sobrevive a salir y volver a la pantalla. */
export function claveSeguida(emisor, tipo) {
  return 'fd.satdoc.seguida.' + String(emisor) + '.' + String(tipo);
}
